import React from 'react';
import { makeStyles, IconButton, Button, Box, Icons } from '~/lib/bn-material-ui';
import { useOrdersDialogDispatch, useOrdersDialogStateSelector } from '../context/orders-dialog-context';
import { BnTextField } from '~/common';
import { DateTime } from 'luxon';

const useStyles = makeStyles(({ palette, breakpoints }) => ({
  noteTextField: {
    width: '100%',
    marginTop: '0.5rem',
    '& textarea': {
      overflowY: 'auto',
      maxHeight: '5rem',
    },
  },
  noteContainer: {
    marginTop: '0.5rem',
    border: `${palette.gray[30]} 1px solid`,
    borderRadius: '0.25rem',
  },
  noteHeader: {
    fontStyle: 'italic',
  },
  noteMessage: {
    maxHeight: '5rem',
    overflowY: 'auto',
  },
  hiddenMdUp: {
    [breakpoints.up('md')]: {
      display: 'none',
    },
  },
}));

export function OrdersDialogNote() {
  const styles = useStyles();
  const [
    isEditingNote,
    unsavedNote,
    selectedOrderEntity,
    createOrEditSirenAction,
  ] = useOrdersDialogStateSelector([
    'isEditingNote',
    'unsavedChanges.note',
    'selectedOrderEntity',
    'createOrEditSirenAction',
  ]);

  const noteEntity = selectedOrderEntity?.getSubEntityByRel('https://api.binsentry.com/rel/order-note');

  const dispatch = useOrdersDialogDispatch();

  if (isEditingNote) {
    return <BnTextField
      variant='outlined'
      multiline
      autoFocus
      className={styles.noteTextField}
      value={unsavedNote === undefined ? noteEntity?.properties.message : unsavedNote}
      onChange={(e) => dispatch({
        type: 'set-unsaved-change-data',
        data: { note: e.target.value },
      })}
      placeholder='Start typing to add note....'
    />;
  }

  if (canAddNote({ noteEntity, createOrEditSirenAction })) {
    return <Box className={styles.hiddenMdUp}>
      <AddNoteButton />
    </Box>;
  }

  if (!noteEntity) {
    return null;
  }

  return <div className={styles.noteContainer}>
    <div
      role='button'
      tabIndex={-1}
      className={styles.noteHeader}
      onClick={() => dispatch({ type: 'enable-editing-note' })}
    >
      <IconButton
        disabled={!createOrEditSirenAction}
        size='large'>
        {createOrEditSirenAction ? <Icons.Edit/> : <Icons.Note />}
      </IconButton>
      {`Note By ${noteEntity.properties.updatedBy} on ${DateTime.fromISO(noteEntity.properties.updatedAt).toFormat('MMM dd, yyyy')}`}
    </div>
    <div className={styles.noteMessage}>
      {noteEntity.properties.message}
    </div>
  </div>;
}

const useAddNoteButtonStyles = makeStyles(() => ({
  addNoteButton: {
    paddingLeft: 0,
    width: 'fit-content',
    fontWeight: 'bold',
    textDecoration: 'none',
    '&& span': {
      textDecoration: 'none',
    },
  },
}));

export const AddNoteButton = () => {
  const dispatch = useOrdersDialogDispatch();
  const styles = useAddNoteButtonStyles();
  return <Button
    className={styles.addNoteButton}
    onClick={() => {
      dispatch({ type: 'enable-editing-note' });
    }}
  >
    <Icons.Add /> Add Note
  </Button>;
};

export const canAddNote = ({ createOrEditSirenAction, noteEntity, isEditingNote }) => createOrEditSirenAction && (!noteEntity || !noteEntity.properties.message) && !isEditingNote;
